import type * as THREE from 'three/webgpu';

import {isSketchGroup} from '../mesh/guards/isSketchGroup';

/**
 * 沿 `targetObject` 的 `.parent` 链向上遍历，返回首个命中的 SketchGroup 的 uuid。
 *
 * 设计说明：
 * 1. 本函数是 cad-core 暴露给宿主层的"所属草图解析"统一入口；
 * 2. 复用 `isSketchGroup` 类型守卫（`mesh/guards/isSketchGroup.ts`），避免各处各自 `instanceof` 检查与
 *    `Reflect.get(current, 'isSketchGroup')` 混杂写法；
 * 3. `manager → mesh` 是 cad-core 包内的单向引用，符合现有模式（参见 `useExtrudeFeature.ts`）。
 *
 * @param targetObject THREE 命中对象。
 * @returns 所属 SketchGroup 的 uuid；遍历到 scene root 仍未命中返回 null。
 */
export function findAncestorSketchGroupId(
    targetObject: THREE.Object3D | null | undefined,
): string | null {
    let node: THREE.Object3D | null = targetObject ?? null;
    while (node) {
        if (isSketchGroup(node)) {
            return node.uuid;
        }
        node = node.parent;
    }
    return null;
}

/**
 * 沿对象父链解析所属草图的持久 ID。
 *
 * 该入口仅供跨运行时身份链路使用；交互态仍可继续使用
 * `findAncestorSketchGroupId` 返回当前 Three.js UUID。
 *
 * @param targetObject THREE 命中对象。
 * @returns 所属 SketchGroup 的持久 ID；未命中返回 null。
 */
export function findAncestorSketchGroupPersistentId(
    targetObject: THREE.Object3D | null | undefined,
): string | null {
    let node: THREE.Object3D | null = targetObject ?? null;
    while (node) {
        if (isSketchGroup(node)) {
            return node.persistentId;
        }
        node = node.parent;
    }
    return null;
}

/**
 * 判断 `targetObject` 是否为当前活动草图下的可编辑目标。
 *
 * 语义约定：
 * 1. `activeSketchGroupId === null`：**permissive pass-through**（返回 true）
 *    → 实际运行中进入 Sketch 模式必然伴随活动草图，本分支仅用于兜底测试 fixture 与
 *      "尚未建立活动草图的初始帧"等边缘场景；
 *    → 非草图场景的拦截由调用方控制（本函数不承担 "是否在 Sketch 模式" 的判定）；
 * 2. `activeSketchGroupId !== null`：返回 `findAncestorSketchGroupId(targetObject) === activeSketchGroupId`；
 * 3. 本函数仅用于"从点选升级为 drag strategy 前的准入判断"，不影响 hover / selected 视觉反馈。
 *
 * @param input 输入。
 * @returns 是否可编辑。
 */
export function isSketchObjectEditableInActiveSketch(input: {
    activeSketchGroupId: string | null;
    targetObject: THREE.Object3D | null | undefined;
}): boolean {
    if (input.activeSketchGroupId === null) {
        return true;
    }
    return findAncestorSketchGroupId(input.targetObject) === input.activeSketchGroupId;
}

//------ 活动草图候选过滤 开始------
/**
 * 判断对象是否允许作为当前草图编辑会话的选择候选。
 *
 * 设计说明：
 * 1. 草图编辑态只能操作当前活动草图中的草图对象，避免其它可见草图参与点选、悬浮和框选；
 * 2. 非草图对象不在本函数中拦截，保留转换实体引用、测量和基准参考等命令按自身模式接入模型拓扑的能力；
 * 3. 引用命令可显式放行外部草图候选，但该能力不改变独立的编辑权限守卫；
 * 4. 没有活动草图时保持透传，避免初始帧或测试 fixture 在尚未建立会话前被过早过滤。
 *
 * @param input 输入。
 * @param input.allowExternalSketchEntities 是否由当前命令显式放行外部草图候选。
 * @returns 允许进入选择候选集时返回 true。
 */
export function isSketchObjectSelectableInActiveSketch(input: {
    activeSketchGroupId: string | null;
    targetObject: THREE.Object3D | null | undefined;
    allowExternalSketchEntities?: boolean;
}): boolean {
    if (input.allowExternalSketchEntities === true) {
        // Convert Entities 等引用命令只放宽候选范围，编辑权限仍由独立守卫限定在活动草图。
        return true;
    }

    if (input.activeSketchGroupId === null) {
        // 没有活动草图时不能推断编辑会话，保持旧行为透传。
        return true;
    }

    const ownerSketchGroupId = findAncestorSketchGroupId(input.targetObject);
    if (ownerSketchGroupId === null) {
        // 非草图对象交给具体命令模式继续裁剪，避免误伤模型拓扑与基准参考选择。
        return true;
    }

    // 有明确草图归属时，只允许当前活动草图对象进入普通草图选择链。
    return ownerSketchGroupId === input.activeSketchGroupId;
}
//------ 活动草图候选过滤 结束------
